import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, X, ChevronLeft } from 'lucide-react';
import { useHaptics } from '../../hooks/useHaptics';
import { getPluginUrl } from '../../utils/images';
import { usePageTitle } from '../../contexts/PageTitleContext';
import GlobalSearch from '../GlobalSearch';

function getTitle(path: string): string {
  if (path === '/' || path === '/home') return 'Sea Level';
  if (path.startsWith('/boats')) return 'Barche';
  if (path.startsWith('/motors')) return 'Motori';
  if (path.startsWith('/accessories')) return 'Accessori';
  if (path.startsWith('/dealers') || path.startsWith('/dealer/')) return 'Commercianti';
  if (path.startsWith('/dashboard')) return 'Dashboard';
  if (path.startsWith('/admin')) return 'Admin';
  if (path.startsWith('/trades')) return 'Trattative';
  if (path.startsWith('/my-')) return 'I Miei';
  if (path.startsWith('/add')) return 'Aggiungi';
  if (path.startsWith('/map')) return 'Mappa';
  if (path.startsWith('/chat')) return 'Chat';
  if (path.startsWith('/manuale')) return 'Manuale';
  if (path.startsWith('/settings')) return 'Impostazioni';
  if (path.startsWith('/import')) return 'Import Dati';
  if (path.startsWith('/tracking')) return 'Statistiche';
  if (path.startsWith('/portfolio')) return 'Portfolio';
  if (path.startsWith('/share/')) return 'Condivisione';
  return 'Sea Level';
}

interface PwaHeaderProps {
  onOpenMenu: () => void;
}

export default function PwaHeader({ onOpenMenu }: PwaHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { impactLight } = useHaptics();
  const { title: customTitle } = usePageTitle();
  const [searchOpen, setSearchOpen] = useState(false);
  const [historyDepth, setHistoryDepth] = useState(0);

  const title = customTitle || getTitle(location.pathname);

  useEffect(() => {
    const updateDepth = () => {
      try {
        setHistoryDepth(window.history.length);
      } catch {
        setHistoryDepth(0);
      }
    };
    updateDepth();
    window.addEventListener('popstate', updateDepth);
    return () => window.removeEventListener('popstate', updateDepth);
  }, [location.key]);

  const handleBack = useCallback(() => {
    impactLight();
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  }, [impactLight, navigate]);

  const handleMenu = useCallback(() => {
    impactLight();
    onOpenMenu();
  }, [impactLight, onOpenMenu]);

  const handleSearch = useCallback(() => {
    impactLight();
    setSearchOpen(prev => !prev);
  }, [impactLight]);

  const showBack = historyDepth > 1 && location.key !== 'default';

  return (
    <>
      <header className="au-header" role="banner">
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              className="au-header-btn"
              onClick={handleBack}
              aria-label="Indietro"
              type="button"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
          <Link to="/" className="au-header-logo" onClick={() => impactLight()} aria-label="Home Sea Level">
            <img
              src={getPluginUrl('sealevel.webp')}
              alt="SeaLevel"
              className="h-7 w-auto object-contain"
              width={110}
              height={28}
            />
          </Link>
        </div>

        <h1 className="au-header-title" aria-live="polite">{title}</h1>

        <div className="flex items-center gap-2">
          <button
            className="au-header-btn"
            onClick={handleSearch}
            aria-label={searchOpen ? 'Chiudi ricerca' : 'Cerca'}
            type="button"
          >
            {searchOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Search className="w-5 h-5" aria-hidden="true" />}
          </button>
          <button
            className="au-header-btn"
            onClick={handleMenu}
            aria-label="Apri menu"
            type="button"
          >
            <Menu className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </header>

      {searchOpen && (
        <div className="shrink-0 px-3 pb-2 pt-1 bg-[rgba(6,9,18,0.72)] -webkit-backdrop-filter:blur(20px) backdrop-filter:blur(20px) border-b border-[var(--au-border)]">
          <div className="relative">
            <GlobalSearch
              placeholder="Cerca barche, motori, commercianti..."
              onResultClick={() => setSearchOpen(false)}
            />
            <button
              onClick={() => setSearchOpen(false)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 active:opacity-50 rounded-lg"
              aria-label="Chiudi ricerca"
              type="button"
            >
              <X className="w-4 h-4 text-[var(--au-text-3)]" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
